// Doc section 9 "Session-to-order correlation": primary method is
// Shopify's cart token (fetched from the Ajax Cart API, /cart.js), which
// we attach to the session server-side and later match against the
// order webhook's cart_token. As a fallback (in case the cart token
// changes between visit and purchase, e.g. cross-device), we also write
// our anonymous visitor_id into a private cart attribute via
// /cart/update.js — Shopify carries cart attributes through to the order
// as note_attributes, which routes/webhooks.ts checks second.
//
// This never touches checkout and never fires unless the shopper already
// has (or creates) a cart — we do not create a cart proactively.

const CART_ATTR_NAME = "_hh_visitor_id";

interface ShopifyCart {
  token: string;
  attributes?: Record<string, string>;
}

export async function getCartToken(): Promise<string | null> {
  try {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) return null;
    const cart = (await res.json()) as ShopifyCart;
    return cart.token ?? null;
  } catch {
    return null;
  }
}

let attributeWriteAttempted = false;

/** Writes the visitor_id fallback cart attribute at most once per page load, and only if a cart already has items. */
export async function ensureCartAttribution(visitorId: string, allowed: () => boolean = () => true): Promise<void> {
  if (attributeWriteAttempted || !allowed()) return;
  attributeWriteAttempted = true;
  try {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) return;
    const cart = (await res.json()) as ShopifyCart & { item_count?: number };
    if (!allowed() || !cart.item_count || cart.attributes?.[CART_ATTR_NAME] === visitorId) return;

    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({ attributes: { [CART_ATTR_NAME]: visitorId } }),
    });
  } catch {
    /* best-effort fallback only — never surfaces to the shopper */
  }
}
